<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-hidden h-full w-full flex items-center justify-center z-50"
  >
    <div
      class="bg-white rounded-lg shadow-xl w-full max-w-9xl mx-4 my-6 flex flex-col"
      style="max-height: 90vh"
    >
      <!-- Fixed Header -->
      <div class="p-6 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-800">
            Survey: {{ survey.title }}
          </h2>
          <button
            @click="$emit('close')"
            class="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <div class="mt-4 grid grid-cols-2 gap-4 text-sm">
          <p><strong>Survey Type:</strong> {{ survey.type }}</p>
          <p><strong>Status:</strong> {{ survey.status }}</p>
          <p><strong>Start Date:</strong> {{ formatDate(survey.startDate) }}</p>
          <p><strong>End Date:</strong> {{ formatDate(survey.endDate) }}</p>
        </div>
      </div>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <div v-if="loading" class="flex justify-center items-center h-64">
          <div class="spinner"></div>
        </div>
        <div v-else-if="error" class="text-red-600 mb-4">
          {{ error }}
        </div>
        <div v-else>
          <!-- University-level survey -->
          <div
            v-if="survey.type == 'University'"
            class="grid grid-cols-1 gap-6"
          >
            <div
              v-for="module in sortedModuleStaff"
              :key="module.moduleCode"
              class="bg-white shadow-md rounded-lg p-4"
            >
              <div
                :class="isModuleActive(module.moduleCode) ? 'bg-primary text-white p-1 flex justify-between items-center' : 'bg-gray-600 text-white p-1 flex justify-between items-center'"
                @click.stop="hide(module)"
              >
                <h3 class="text-lg font-bold">
                  {{ module.moduleCode }} - {{ module.moduleName }}    
                </h3>
                <div class="flex justify-end" style="font-style: italic;">Status: {{ getModuleStatus(module.moduleCode)}}</div>
                <button
                  class="text-gray-600 hover:text-gray-800 focus:outline-none"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    style="color: white;"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    v-if="
                      !hideQuestions.find((x) => x.id == module.moduleCode)
                        .visible
                    "
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4v16m8-8H4"
                    ></path>
                  </svg>
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    style="color: white;"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    v-if="
                      hideQuestions.find((x) => x.id == module.moduleCode)
                        .visible
                    "
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M20 12H4"
                    ></path>
                  </svg>
                </button>
              </div>
            <div v-if="hideQuestions.find((x) => x.id == module.moduleCode)
                        .visible">
              <div class="relative" v-if="module.needSelect">
                <div v-if="module.staff.length > 1" class="mt-1 mb-2 space-y-2">
                  <label class="block text-sm font-medium text-gray-700"
                    >Select Staff Members</label
                  >
                  <button
                    v-if="getSelectedStaff(module, 0).length == 0"
                    @click="openTeacherSelection(module, -2)"
                    class="ml-2 text-sm bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition-colors duration-200"
                  >
                    Select Teachers
                  </button>
                  <button
                    v-if="getSelectedStaff(module, 0).length > 0"
                    @click="openTeacherSelection(module, -2)"
                    class="ml-2 text-sm bg-primary text-white px-2 py-1 rounded hover:bg-primary-dark transition-colors duration-200"
                  >
                    Change Teachers
                  </button>
                </div>
                <div v-else class="mt-1 text-sm text-gray-700">
                  <!--    {{ module.staff[0].name }} -->
                </div>
              </div>
              <div
                v-for="(question, questionIndex) in survey.questions"
                :key="questionIndex"
                v-if="getSelectedStaff(module, 0).length > 0"
              >
                <!--<p class="font-medium mb-2">{{ question.text }}</p> -->
                <div
                  class="bg-gray-300 text-black p-1 flex justify-between items-center"
                >
                  {{ questionIndex + 1 }}. {{ question.text }}
                </div>
                <div class="mb-2">
                  <div
                    v-for="staffId in getSelectedStaff(module, questionIndex)"
                    :key="staffId"
                  >
                    <div
                      v-if="question.type === 'rating'"
                      class="flex items-center space-x-1 mb-2 mt-2"
                    >
                      <!--   <h5 class="font-medium text-sm mb-2">
                            Answer for {{ getStaffName(module, staffId) }}
                          </h5>-->
                      <span class="font-medium text-sm" style="width: 300px"
                        >For
                        <span
                          @click="showStaffImage(module, staffId)"
                          style="text-decoration: underline; cursor: pointer"
                          >{{ getStaffName(module, staffId) }}</span
                        ></span
                      >

                      <template v-for="n in question.maxRating || 5">
                        <button
                          :key="n"
                          @click="
                            setRating(
                              module.moduleCode,
                              questionIndex,
                              n,
                              staffId
                            )
                          "
                          class="w-6 h-6 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
                          :aria-label="`Rate ${n} out of ${
                            question.maxRating || 5
                          }`"
                        >
                          <svg
                            class="w-6 h-6"
                            :class="[
                              n <=
                              getRating(
                                module.moduleCode,
                                questionIndex,
                                staffId
                              )
                                ? 'text-yellow-400'
                                : 'text-gray-300',
                            ]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                            />
                          </svg>
                        </button>
                      </template>
                      <span class="ml-2 text-sm text-gray-600">
                        ({{
                          getRating(
                            module.moduleCode,
                            questionIndex,
                            staffId
                          ) || 0
                        }}/{{ question.maxRating || 5 }})
                      </span>
                    </div>
                    <div v-else-if="question.type === 'single'" class="mt-1">
                      <select
                        v-model="
                          getAnswer(module.moduleCode, questionIndex, staffId)
                            .value
                        "
                        class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                        <option value="">Select an option</option>
                        <option
                          v-for="option in question.options"
                          :key="option"
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>
                    </div>
                    <div
                      v-else-if="question.type === 'multiple'"
                      class="space-y-2"
                    >
                      <div
                        v-for="option in question.options"
                        :key="option"
                        class="flex items-center"
                      >
                        <input
                          type="checkbox"
                          :id="`${module.moduleCode}-${questionIndex}-${staffId}-${option}`"
                          :value="option"
                          v-model="
                            getAnswer(module.moduleCode, questionIndex, staffId)
                              .value
                          "
                          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          :for="`${module.moduleCode}-${questionIndex}-${staffId}-${option}`"
                          class="ml-2 block text-sm text-gray-900"
                        >
                          {{ option }}
                        </label>
                      </div>
                    </div>
                    <div v-else-if="question.type === 'text'" class="mt-1">
                      <input
                        type="text"
                        v-model="
                          getAnswer(module.moduleCode, questionIndex, staffId)
                            .value
                        "
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-4" v-if="getSelectedStaff(module, 0).length > 0">
                <label
                  class="bg-primary text-white p-1 flex justify-between items-center mb-2"
                  >Optional Comments</label
                >
                <div
                  v-for="staffId in getSelectedStaff(module, 0)"
                  :key="staffId"
                >
                  <h5 class="font-medium text-sm mb-2">
                    For 
                    <span  @click="showStaffImage(module, staffId)"
                          style="text-decoration: underline; cursor: pointer">
                    {{ getStaffName(module, staffId) }}
                    </span>
                  </h5>
                  <textarea
                    v-model="moduleComments[module.moduleCode][staffId]"
                    class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    rows="3"
                    placeholder="Add your comments for this module..."
                  ></textarea>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- School/Department-level survey -->
          <div v-else>
            <div
              v-for="module in moduleStaff"
              :key="module.moduleCode"
              class="bg-white shadow-md rounded-lg p-4"
            >
              <div
                class="bg-primary text-white p-1 flex justify-between items-center"
                @click.stop="hide(module)"
              >
                <h3 class="text-lg font-bold">
                  {{ module.moduleCode }} - {{ module.moduleName }}
                </h3>
                <button
                  class="text-gray-600 hover:text-gray-800 focus:outline-none"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    v-if="
                      !hideQuestions.find((x) => x.id == module.moduleCode)
                        .visible
                    "
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4v16m8-8H4"
                    ></path>
                  </svg>
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    v-if="
                      hideQuestions.find((x) => x.id == module.moduleCode)
                        .visible
                    "
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M20 12H4"
                    ></path>
                  </svg>
                </button>
              </div>
              <div v-if="module.needSelect && survey.questions.filter(x=>x.option == 'module/staff').length > 0">
                <button
                  @click="openTeacherSelection(module, -2)"
                  v-if="module.staff.length > 1"
                  class="mt-2 mb-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition duration-300"
                >
                  Select Teachers
                </button>
              </div>
              <div
                v-for="(question, questionIndex) in survey.questions"
                :key="questionIndex"
                class="mb-4"
                v-if="
                  hideQuestions.find((x) => x.id == module.moduleCode).visible && 
                  (!survey.questions.filter(x=>x.option == 'module/staff').length > 0 || getSelectedTeachers(module, questionIndex).length > 0)        
                "
              >
                <div
                  class="bg-gray-300 text-black p-1 flex justify-between items-center"
                >
                  {{ questionIndex + 1 }}. {{ question.text }}
                </div>
                <div v-if="question.option === 'module/staff'">
                  <!-- <button
                    @click="openTeacherSelection(module, questionIndex)"
                    v-if="module.staff.length > 1"
                    class="mb-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition duration-300"
                  >
                    Select Teachers
                  </button> -->
                  <div
                    v-for="staff in getSelectedTeachers(module, questionIndex)"
                    :key="staff.staffId"
                    class="mt-4"
                  >
                    <div
                      v-if="question.type === 'rating'"
                      class="flex items-center space-x-1"
                    >
                      <span class="mr-2 font-medium text-sm mb-2"
                          @click="showStaffImage(module, staff.staffId)"
                          style="text-decoration: underline; cursor: pointer"
                        >For {{ staff.name }}</span
                      >
                      <template v-for="n in question.maxRating || 5">
                        <button
                          :key="n"
                          @click="
                            setRating(
                              module.moduleCode,
                              questionIndex,
                              n,
                              staff.staffId
                            )
                          "
                          class="w-6 h-6 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
                          :aria-label="`Rate ${n} out of ${
                            question.maxRating || 5
                          }`"
                        >
                          <svg
                            class="w-6 h-6"
                            :class="[
                              n <=
                              getRating(
                                module.moduleCode,
                                questionIndex,
                                staff.staffId
                              )
                                ? 'text-yellow-400'
                                : 'text-gray-300',
                            ]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                            />
                          </svg>
                        </button>
                      </template>
                      <span class="ml-2 text-sm text-gray-600">
                        ({{
                          getRating(
                            module.moduleCode,
                            questionIndex,
                            staff.staffId
                          ) || 0
                        }}/{{ question.maxRating || 5 }})
                      </span>
                    </div>
                    <div v-else-if="question.type === 'single'" class="mt-1">
                      <span class="mr-2 font-medium text-sm mb-2"
                        @click="showStaffImage(module, staff.staffId)"
                        style="text-decoration: underline; cursor: pointer"
                        >For {{ staff.name }}</span
                      >
                      <select
                        v-model="
                          getAnswer(
                            module.moduleCode,
                            questionIndex,
                            staff.staffId
                          ).value
                        "
                        class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                        <option value="">Select an option</option>
                        <option
                          v-for="option in question.options"
                          :key="option"
                          :value="option"
                        >
                          {{ option }}
                        </option>
                      </select>
                    </div>
                    <div
                      v-else-if="question.type === 'multiple'"
                      class="space-y-2"
                    >
                      <span class="mr-2 font-medium text-sm mb-2"
                        @click="showStaffImage(module, staff.staffId)"
                          style="text-decoration: underline; cursor: pointer"
                        >For {{ staff.name }}</span
                      >
                      <div
                        v-for="option in question.options"
                        :key="option"
                        class="flex items-center"
                      >
                        <input
                          type="checkbox"
                          :id="`${module.moduleCode}-${questionIndex}-${staff.staffId}-${option}`"
                          :value="option"
                          v-model="
                            getAnswer(
                              module.moduleCode,
                              questionIndex,
                              staff.staffId
                            ).value
                          "
                          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label
                          :for="`${module.moduleCode}-${questionIndex}-${staff.staffId}-${option}`"
                          class="ml-2 block text-sm text-gray-900"
                        >
                          {{ option }}
                        </label>
                      </div>
                    </div>
                    <div v-else-if="question.type === 'text'" class="mt-1">
                      <span class="mr-2 font-medium text-sm mb-2"
                        @click="showStaffImage(module, staff.staffId)"
                          style="text-decoration: underline; cursor: pointer"
                        >For {{ staff.name }}</span
                      >
                       
                      <input
                        type="text"
                        v-model="
                          getAnswer(
                            module.moduleCode,
                            questionIndex,
                            staff.staffId
                          ).value
                        "
                        class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
                <div v-else>
                  <!-- Non-module/staff questions -->
                  <div
                    v-if="question.type === 'rating'"
                    class="flex items-center space-x-1"
                  >
                    <template v-for="n in question.maxRating || 5">
                      <button
                        :key="n"
                        @click="setRating(module.moduleCode, questionIndex, n)"
                        class="w-6 h-6 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-full"
                        :aria-label="`Rate ${n} out of ${
                          question.maxRating || 5
                        }`"
                      >
                        <svg
                          class="w-6 h-6"
                          :class="[
                            n <= getRating(module.moduleCode, questionIndex)
                              ? 'text-yellow-400'
                              : 'text-gray-300',
                          ]"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                          />
                        </svg>
                      </button>
                    </template>
                    <span class="ml-2 text-sm text-gray-600">
                      ({{ getRating(module.moduleCode, questionIndex) || 0 }}/{{
                        question.maxRating || 5
                      }})
                    </span>
                  </div>
                  <div v-else-if="question.type === 'single'" class="mt-1">
                    <select
                      v-model="
                        getAnswer(module.moduleCode, questionIndex).value
                      "
                      class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                      <option value="">Select an option</option>
                      <option
                        v-for="option in question.options"
                        :key="option"
                        :value="option"
                      >
                        {{ option }}
                      </option>
                    </select>
                  </div>
                  <div
                    v-else-if="question.type === 'multiple'"
                    class="space-y-2"
                  >
                    <div
                      v-for="option in question.options"
                      :key="option"
                      class="flex items-center"
                    >
                      <input
                        type="checkbox"
                        :id="`${module.moduleCode}-${questionIndex}-${option}`"
                        :value="option"
                        v-model="
                          getAnswer(module.moduleCode, questionIndex).value
                        "
                        class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label
                        :for="`${module.moduleCode}-${questionIndex}-${option}`"
                        class="ml-2 block text-sm text-gray-900"
                      >
                        {{ option }}
                      </label>
                    </div>
                  </div>
                  <div v-else-if="question.type === 'text'" class="mt-1">
                    <input
                      type="text"
                      v-model="
                        getAnswer(module.moduleCode, questionIndex).value
                      "
                      class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
              <!-- <div class="mt-4">
                <label class="block text-sm font-medium text-gray-700">Module Comments</label>
                <textarea v-model="moduleComments[module.moduleCode]"
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  rows="3" placeholder="Add your comments for this module..."></textarea>
              </div>-->
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->

      <div class="p-6 border-t border-gray-200" v-if="isSurveyActive()">
        <div class="flex justify-end">
          <button
            @click="saveSurvey"
            class="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            :disabled="isSaving || isSubmitting"
          >
            {{ isSaving ? "Saving..." : "Save" }}
          </button>

          <button
            @click="submitSurvey"
            class="ml-4 px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Submit Survey
          </button>
        </div>
      </div>
    </div>

    <!-- modal for staff image-->
    <div
      v-if="currentImage"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-hidden h-full w-full flex items-center justify-center z-50"
    >
      <div
        class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 my-6 flex flex-col"
      >
        <div
          class="p-6 border-b border-gray-200 flex justify-between items-center"
        >
          <h2 class="text-2xl font-bold text-gray-800">Staff Image</h2>
          <button
            @click="currentImage = ''"
            class="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <div class="p-6 flex justify-center">
          <img
            :src="currentImage"
            alt="Staff Image"
            class="max-w-full max-h-full rounded-lg"
            style="max-height: 800px"
          />
        </div>
      </div>
    </div>

    <div
      v-if="tooltipContent"
      :style="tooltipStyle"
      class="absolute bg-black text-white p-2 rounded-md text-sm z-50"
    >
      {{ tooltipContent }}
    </div>
    <TeacherSelectionModal
      v-if="showTeacherModal"
      :show="showTeacherModal"
      :module-code="currentModule?.moduleCode"
      :initial-selected-teachers="getSelectedTeachers(currentModule)"
      :teachers="currentModule?.staff || []"
      @close="closeTeacherSelection"
      @select="handleTeacherSelection"
    />
    <ResultModal
      ref="resultModal"
      :message="resultMessage"
      @close="closeModal()"
    />
    <FailureModal ref="failureModal" :message="resultMessage" />
  </div>
</template>

<script>
import { surveyApi } from "@/api/survey";
import TeacherSelectionModal from "./TeacherSelectionModal.vue";
import ResultModal from "./ResultModal.vue";
import FailureModal from "./FailureModal.vue";
export default {
  name: "StudentSurveyModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    survey: {
      type: Object,
      required: true,
    },
    studentId: {
      type: String,
      required: true,
    },
  },
  components: {
    TeacherSelectionModal,
    ResultModal,
    FailureModal,
  },
  computed: {
   sortedModuleStaff() {

      let _on = this.moduleStaff.filter((x) => this.getModuleStatus(x.moduleCode) == "Ongoing");
      let _ex = this.moduleStaff.filter((x) => this.getModuleStatus(x.moduleCode) == "Expired");
      let _na = this.moduleStaff.filter((x) => this.getModuleStatus(x.moduleCode) == "Not Started");

      //sort by module code
      _on.sort((a, b) => {
        return a.moduleCode.localeCompare(b.moduleCode);
      });
      _ex.sort((a, b) => {
        return a.moduleCode.localeCompare(b.moduleCode);
      });
      _na.sort((a, b) => {
        return a.moduleCode.localeCompare(b.moduleCode);
      });
      return _on.concat(_ex).concat(_na);

    },
  },
  data() {
    return {
      loading: true,
      error: null,
      moduleStaff: [],
      moduleQuestions: {},
      moduleComments: {},
      tooltipContent: null,
      tooltipStyle: {
        display: "none",
        position: "fixed",
      },
      showTeacherModal: false,
      currentModule: null,
      currentQuestionIndex: -1,
      selectedTeachers: {},
      hideQuestions: [],
      isSaving: false,
      currentImage: "",
      savedSuccessfully: false,
      resultMessage: "",
      isSubmitting: false, // Add isSubmitting to track submission state
    };
  },
  methods: {
    isSurveyActive() {
      if (!this.survey) return false
      let result = false;
      console.log(this.survey)
      this.moduleStaff.forEach((module) => {
        if (this.isModuleActive(module.moduleCode))
          {
            result = true
          }
      });
      console.log('isSurveyActive' + result)
      return result;
    },
    closeModal() {
      this.$emit("close");
    },
    formatDateTime(datetime) {
      if (!datetime) return "";
      const date = new Date(datetime);
      return date.toLocaleString();
    },
    async fetchSurveyResponse() {
      this.loading = true;
      this.error = null;
      try {
        const data = await surveyApi.getSurveyResponse(
          this.survey.id,
          this.studentId
        );
        if (data) {
          let _status = data.status == 0 ? "Saved" : "Submitted";
          _status +=
            " at " +
            this.formatDateTime(
              data.status == 0 ? data.lastSaved : data.submittedAt
            );
          this.survey.status = _status;
          this.initializeModuleQuestions(data.responses);
        } else {
          this.initializeResponses();
        }
      } catch (error) {
        console.error("Error fetching survey response:", error);
        this.error = "Failed to load survey data. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    initializeModuleQuestions(existingResponses = null) {
      this.moduleResponses = {};
      this.moduleStaff.forEach((module) => {
        let answered = false;
        let answeredStaff = [];
        this.$set(this.moduleQuestions, module.moduleCode, {});
        this.$set(this.moduleComments, module.moduleCode, {});
        this.$set(this.selectedTeachers, module.moduleCode, []);
        module.staff.forEach((staff) => {
          //this.moduleResponses[module.moduleCode][staff.staffId] = {};
          this.survey.questions.forEach((question, index) => {
            this.getAnswer(module.moduleCode, index, staff.staffId).value = {};
            if (existingResponses) {
              let existingResponse = existingResponses.find(
                (r) =>
                  r.moduleCode === module.moduleCode &&
                  r.staffId === staff.staffId &&
                  r.questionIndex === index
              );

              if (question.type == "multiple") {
                if (question.option == "module/staff") {
                  let _array = existingResponse
                    ? existingResponse.answer.split(",")
                    : [];
                  this.getAnswer(
                    module.moduleCode,
                    index,
                    staff.staffId
                  ).value = _array;
                } else {
                  let _nonModuleExistingResponse = existingResponses.find(
                    (r) =>
                      r.moduleCode === module.moduleCode &&
                      r.questionIndex === index
                  );
                  let _array = _nonModuleExistingResponse
                    ? _nonModuleExistingResponse.answer.split(",")
                    : [];
                  this.getAnswer(module.moduleCode, index).value = _array;
                }
              } else if (question.type == "rating") {
                if (question.option == "module/staff")
                  this.getAnswer(
                    module.moduleCode,
                    index,
                    staff.staffId
                  ).value = existingResponse
                    ? parseInt(existingResponse.answer)
                    : 0;
                else {
                  let _nonModuleExistingResponse = existingResponses.find(
                    (r) =>
                      r.moduleCode === module.moduleCode &&
                      r.questionIndex === index
                  );
                  this.getAnswer(module.moduleCode, index).value =
                    _nonModuleExistingResponse
                      ? parseInt(_nonModuleExistingResponse.answer)
                      : 0;
                }
              } else {
                if (question.option == "module/staff")
                  this.getAnswer(
                    module.moduleCode,
                    index,
                    staff.staffId
                  ).value = existingResponse ? existingResponse.answer : null;
                else {
                  let _nonModuleExistingResponse = existingResponses.find(
                    (r) =>
                      r.moduleCode === module.moduleCode &&
                      r.questionIndex === index
                  );
                  this.getAnswer(module.moduleCode, index).value =
                    _nonModuleExistingResponse
                      ? _nonModuleExistingResponse.answer
                      : null;
                }
              }

              if (
                (existingResponse && existingResponse.answer) ||
                module.staff.length == 1
              ) {
                answered = true;
                if (!answeredStaff.includes(staff)) {
                  answeredStaff.push(staff);
                }
              }
            } else {
              this.getAnswer(module.moduleCode, index, staff.staffId).value =
                null;
            }
          });

          let comments = existingResponses.find(
            (r) =>
              r.moduleCode === module.moduleCode &&
              r.staffId === staff.staffId &&
              r.questionIndex === -1
          );
          if (comments)
            this.moduleComments[module.moduleCode][staff.staffId] = comments
              ? comments.answer
              : "";
        });
        if (answered) this.selectedTeachers[module.moduleCode] = answeredStaff;
        if (!module.needSelect) this.selectedTeachers[module.moduleCode] = module.staff;
      });      
    },
    hide(module) {
      let m = this.hideQuestions.find((x) => x.id == module.moduleCode);
      m.visible = !m.visible;
    },
    async fetchStudentData() {
      try {
        let data = await surveyApi.getStudentDataForSurvey(
          this.survey.id,
          this.studentId
        );

        this.moduleDates = await surveyApi.getModuleDates(this.survey.id);

        let needSelect = false;

        data.modules.forEach((m) => {
          m.activities.forEach((a) => {
            if (a.staff.length > 1) needSelect = true;
            a.staff = a.staff.filter((x) => x);
          });
        });

        this.moduleStaff = data.modules.map((module) => ({
          moduleCode: module.moduleCode,
          moduleName: module.name,
          staff: module.activities.flatMap((activity) => activity.staff),
          needSelect: needSelect,
        }));

        // Distinct staff by staffId
        this.moduleStaff.forEach((module) => {
          this.hideQuestions.push({ id: module.moduleCode, visible: this.isModuleActive(module.moduleCode) });
          const uniqueStaff = [];
          const staffIds = new Set();
          module.staff.forEach((staff) => {
            if (!staffIds.has(staff.staffId)) {
              staffIds.add(staff.staffId);
              uniqueStaff.push(staff);
            }
          });
          module.staff = uniqueStaff;
        });

        //this.initializeResponses();
        this.fetchSurveyResponse();
      } catch (error) {
        console.error("Error fetching student data:", error);
        this.error = "Failed to load survey data. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    initializeResponses() {
      this.moduleStaff.forEach((module) => {
        this.$set(this.moduleQuestions, module.moduleCode, {});
        this.$set(this.moduleComments, module.moduleCode, {});
        this.$set(this.selectedTeachers, module.moduleCode, []);
        this.survey.questions.forEach((question, index) => {
          this.$set(this.moduleQuestions[module.moduleCode], index, {
            value: question.type === "multiple" ? [] : null,
            staffResponses: {},
          });
          module.staff.forEach((staff) => {
            this.$set(
              this.moduleQuestions[module.moduleCode][index].staffResponses,
              staff.staffId,
              {
                value: question.type === "multiple" ? [] : null,
              }
            );

            if (module.staff.length == 1 || !module.needSelect)
              this.selectedTeachers[module.moduleCode] = module.staff;
          });
        });
      });
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleString();
    },
    showTooltip(event, content) {
      this.tooltipContent = content;
      this.tooltipStyle = {
        display: "block",
        position: "fixed",
        top: `${event.clientY + 10}px`,
        left: `${event.clientX + 10}px`,
      };
    },
    hideTooltip() {
      this.tooltipContent = null;
      this.tooltipStyle.display = "none";
    },
    isModuleActive(moduleCode) {
      if (this.survey.type !== "University") return true;
      let _moduleDates = this.moduleDates.filter(
        (x) => x.moduleCode == moduleCode
      );
      const moduleDates = _moduleDates[_moduleDates.length - 1];
      if (!moduleDates) return false; // If no dates are set, assume it's active
      const now = new Date();
      return (
        now >= new Date(moduleDates.startDate) &&
        now <= new Date(moduleDates.endDate)
      );
    },
    getModuleStatus(moduleCode) {
      if (this.survey.type !== "University") return true;
      let _moduleDates = this.moduleDates.filter(
        (x) => x.moduleCode == moduleCode
      );
      const moduleDates = _moduleDates[_moduleDates.length - 1];
      if (!moduleDates) return 'Not Started'; // If no dates are set, assume it's active
      const now = new Date();
      if (now >= new Date(moduleDates.startDate) &&
        now <= new Date(moduleDates.endDate))
        return 'Ongoing';
      if (now < new Date(moduleDates.startDate))
        return 'Not Started';
      if (now > new Date(moduleDates.endDate))
        return 'Expired';
    },
    setRating(moduleCode, questionIndex, rating, staffId = null) {
      if (!this.isModuleActive(moduleCode)) return;
      if (staffId) {
        this.moduleQuestions[moduleCode][questionIndex].staffResponses[
          staffId
        ].value = rating;
      } else {
        this.moduleQuestions[moduleCode][questionIndex].value = rating;
      }
    },
    getRating(moduleCode, questionIndex, staffId = null) {
      if (staffId) {
        return this.moduleQuestions[moduleCode][questionIndex].staffResponses[
          staffId
        ].value;
      }
      return this.moduleQuestions[moduleCode][questionIndex].value;
    },
    getAnswer(moduleCode, questionIndex, staffId = null) {
      if (!this.moduleQuestions[moduleCode]) {
        this.$set(this.moduleQuestions, moduleCode, {});
      }
      if (!this.moduleQuestions[moduleCode][questionIndex]) {
        this.$set(this.moduleQuestions[moduleCode], questionIndex, {
          value: null,
          staffResponses: {},
        });
      }
      if (staffId) {
        if (
          !this.moduleQuestions[moduleCode][questionIndex].staffResponses[
            staffId
          ]
        ) {
          this.$set(
            this.moduleQuestions[moduleCode][questionIndex].staffResponses,
            staffId,
            {
              value:
                this.survey.questions[questionIndex].type === "multiple"
                  ? []
                  : null,
            }
          );
        }
        return this.moduleQuestions[moduleCode][questionIndex].staffResponses[
          staffId
        ];
      }
      if (
        this.moduleQuestions[moduleCode][questionIndex].value === null &&
        this.survey.questions[questionIndex].type === "multiple"
      ) {
        this.$set(this.moduleQuestions[moduleCode][questionIndex], "value", []);
      } else if (
        this.moduleQuestions[moduleCode][questionIndex].value === null &&
        this.survey.questions[questionIndex].type === "single"
      ) {
        this.$set(this.moduleQuestions[moduleCode][questionIndex], "value", "");
      } 
      

      return this.moduleQuestions[moduleCode][questionIndex];
    },
    openTeacherSelection(module) {
      this.currentModule = module;
      this.showTeacherModal = true;
    },

    closeTeacherSelection() {
      this.showTeacherModal = false;
      this.currentModule = null;
      this.currentQuestionIndex = -1;
    },

    handleTeacherSelection(teachers) {
      if (this.currentModule) {
        const moduleCode = this.currentModule.moduleCode;
        this.$set(this.selectedTeachers, moduleCode, teachers);

        // Update the responses for the selected teachers
        this.survey.questions.forEach((_, questionIndex) => {
          teachers.forEach((teacher) => {
            if (
              !this.moduleQuestions[moduleCode][questionIndex].staffResponses[
                teacher.staffId
              ]
            ) {
              this.$set(
                this.moduleQuestions[moduleCode][questionIndex].staffResponses,
                teacher.staffId,
                { value: null }
              );
            }
          });

          // Remove responses for unselected teachers
          Object.keys(
            this.moduleQuestions[moduleCode][questionIndex].staffResponses
          ).forEach((staffId) => {
            if (!teachers.some((t) => t.staffId === staffId)) {
              this.$delete(
                this.moduleQuestions[moduleCode][questionIndex].staffResponses,
                staffId
              );
            }
          });
        });
      }
      this.closeTeacherSelection();
    },

    getSelectedTeachers(module) {
      return this.selectedTeachers[module.moduleCode] || [];
    },

    async saveSurvey() {
      if (this.isSaving || this.isSubmitting) return;

      this.isSaving = true;
      this.savedSuccessfully = false;

      try {
        // Validate inputs before saving
        //if (!this.validateInputs()) {
        //  this.isSaving = false;
        //  return;
        //}

        const surveyData = this.prepareSurveyData();
        await surveyApi.saveSurvey(this.survey.id, surveyData);
        this.savedSuccessfully = true;
        this.$emit("survey-saved");
        //alert("You have saved your suvery.");
        this.resultMessage = "You have saved your suvery.";
        this.$refs.resultModal.showModal();
        // this.closeModal()
        // Show success message
      } catch (error) {
        console.error("Error saving survey:", error);
        // Show error message
      } finally {
        this.isSaving = false;
      }
    },

    async submitSurvey() {
      if (this.isSaving || this.isSubmitting) return;

      this.isSubmitting = true;

      try {
        if (!this.validateInputs()) {
          this.isSubmitting = false;
          return;
        }

        const surveyData = this.prepareSurveyData();
        await surveyApi.submitSurvey(this.survey.id, surveyData);

        this.$emit("survey-submitted");
        //alert("You have submitted your survey.");

        this.resultMessage = "You have submitted your suvery.";
        this.$refs.resultModal.showModal();
        //this.closeModal();
      } catch (error) {
        console.error("Error submitting survey:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    prepareSurveyData() {
      const surveyData = {
        studentId: this.studentId,
        responses: [],
      };

      for (const moduleCode in this.moduleQuestions) {
        console.log(moduleCode);
        const moduleResponses = this.moduleQuestions[moduleCode];
        for (const idx in moduleResponses) {
          let question = this.survey.questions[idx];
          if (question.option == "module") {
            let _value = moduleResponses[idx].value;
            if (_value) {
              surveyData.responses.push({
                moduleCode,
                staffId: null,
                questionIndex: idx,
                answer: _value.toString(),
              });
            }
          } else {
            let _array = moduleResponses[idx].staffResponses;
            for (const staffId in _array) {
              const response = _array[staffId];
              if (response.value) {
                surveyData.responses.push({
                  moduleCode,
                  staffId: staffId === "default" ? null : staffId,
                  questionIndex: idx,
                  answer: response.value.toString(),
                });
              }
            }
          }
        }
      }

      // Add comments to the survey data
      for (const moduleCode in this.moduleComments) {
        const moduleComments = this.moduleComments[moduleCode];
        for (const staffId in moduleComments) {
          const comment = moduleComments[staffId];
          if (comment && comment.trim() !== "") {
            surveyData.responses.push({
              moduleCode,
              staffId: staffId === "default" ? null : staffId,
              questionIndex: -1, // Use -1 to indicate it's a comment
              answer: comment,
            });
          }
        }
      }

      return surveyData;
    },

    validateInputs() {
      let isValid = true;
      let errorMessages = [];

      // Check if student ID is provided
      if (!this.studentId) {
        isValid = false;
        errorMessages.push("Please enter your Student ID.");
      }
      // Validate responses
      for (const moduleCode in this.moduleQuestions) {
        const moduleResponses = this.moduleQuestions[moduleCode];
        for (const index in moduleResponses) {
          let response =
            moduleResponses[index].value ||
            moduleResponses[index].staffResponses;
          const question = this.survey.questions[index];
          let _module = this.moduleStaff.find((x) => x.moduleCode == moduleCode);
          if ( 
            this.isModuleActive(moduleCode) &&
            _module.staff.length > 0 &&
            question.isMandatory &&
            (response === null ||
              response === "" ||
              (!this.checkValidation(response, moduleCode) &&
                moduleResponses[index].value == null))
          ) {
            isValid = false;
            errorMessages.push(
              `Please answer the required question: ${question.text}`
            );
          }
        
        }
      }

      if (!isValid) {
        this.resultMessage = "Please answer all the required questions";
        this.$refs.failureModal.showModal();
      }

      return isValid;
    },

    checkValidation(response, moduleCode) {
      let count = 0;
      console.log("checking " + moduleCode);
      console.log(response);
      let selectedTeachers = this.selectedTeachers[moduleCode];

      let result = true;
      let keys = Object.keys(response);

      keys.forEach((key) => {
        if (selectedTeachers.find((x) => x.staffId == key)) {
          let value = response[key].value;
          console.log(value);
          count ++;
          if (value == null || value == "") {
            console.log("Key " + key + " value is not validated");
            result = false;
          }
        } else
          console.log("staff " + key + " is not selected for " + moduleCode);
      });

      console.log("result " + result);
      console.log('count ' + count)
      return result && count > 0;
    },

    getStaffName(module, staffId) {
      const staff = module.staff.find((s) => s.staffId === staffId);
      return staff ? staff.name : "Unknown Staff";
    },
    showStaffImage(module, staffId) {
      const staff = module.staff.find((s) => s.staffId === staffId);
      this.currentImage = staff ? staff.image : "Unknown Staff";
    },
    getSelectedStaff(module, questionIndex) {
      if (!this.selectedTeachers[module.moduleCode]) return "";
      return this.selectedTeachers[module.moduleCode].map((o) => o.staffId);
    },
  },
  mounted() {
    this.fetchStudentData();
  },
};
</script>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  animation: spin 1s ease infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>